/**
 * chartTypes.ts – the data shape ProjectionChart draws, plus the categorical palette its series
 * are coloured from.
 *
 * One shape serves every pane of the projections modal's chart accordion (cashflow, net worth,
 * debt): a stack of series per year, an optional dotted reference level, and the tooltip detail
 * that sits behind figures the bars only total. Feature modules build these points from projection
 * rows (see features/agedCare/cashflowChartData.ts and balanceSheetChartData.ts); the chart itself
 * knows nothing about aged care.
 */

/** A stacked series. Ids are stable — the legend's on/off state and the chart's test hooks key off them. */
export interface ChartSeries {
    id: string;
    label: string;
    /** Categorical palette slot. Fixed per series — never reassigned when the legend hides one. */
    color: string;
}

export interface ChartValueLine {
    label: string;
    value: number;
}

/** A titled group of tooltip rows — the detail behind a figure the bars only show as one block. */
export interface ChartTooltipSection {
    title: string;
    lines: ChartValueLine[];
    /** Rendered as a ruled-off total under `lines`. */
    total?: ChartValueLine;
}

/** The tinted headline at the foot of the tooltip. `tone` picks the green/red wash, not the copy. */
export interface ChartPointSummary extends ChartValueLine {
    tone: 'positive' | 'negative';
}

/** One band of the chart — a projected year. */
export interface ChartPoint {
    /** Axis tick text, e.g. "2026/27" or "Year 3". */
    label: string;
    /** Longer name for the tooltip heading and the band's accessible label. */
    fullLabel: string;
    /** Stacked value per series id, stacked bottom-up in the chart's `series` order. */
    values: Record<string, number>;
    /** Level for the dotted reference line, on charts configured with one. */
    reference?: number;
    /** Detail sections listed under the stack breakdown in the tooltip. */
    sections?: ChartTooltipSection[];
    /**
     * Foot-of-tooltip summary. Only read by charts WITHOUT a `reference.gap` — where there is one,
     * the summary is the gap itself, which depends on which series the legend currently has on and
     * so can only be derived at render time.
     */
    summary?: ChartPointSummary;
}

/**
 * The categorical palette, in slot order. Colours are validated against the projections modal's
 * white surface (worst adjacent-pair CVD ΔE 9.1, normal-vision ΔE 19.6). Slot ORDER is the
 * colourblind-safety mechanism, not a cosmetic choice — reorder only by re-validating. Three slots
 * sit below 3:1 contrast on white, which the legend labels, the hover tooltip and the projections
 * table below the chart relieve.
 *
 * Red is deliberately absent: it is reserved for status (a shortfall is a state, not a category).
 */
export const CHART_CATEGORICAL_SLOTS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7'] as const;

/** Status "critical" — a state (expenses not covered, money owed), deliberately not a categorical slot. */
export const CHART_STATUS_CRITICAL = '#d03b3b';

/** Total of a point's stacked series, counting only those the legend currently has switched on. */
export function stackTotal(point: ChartPoint, series: readonly ChartSeries[], enabled: (id: string) => boolean): number {
    return series.reduce((sum, entry) => (enabled(entry.id) ? sum + (point.values[entry.id] || 0) : sum), 0);
}

/**
 * Scale every money figure in a point by `factor` — the projections modal's today's-dollars
 * deflation, applied to the chart in exactly the same step the table applies it to its own rows.
 */
export function scaleChartPoint(point: ChartPoint, factor: number): ChartPoint {
    if (factor === 1) return point;
    const values: Record<string, number> = {};
    Object.keys(point.values).forEach((key) => {
        values[key] = point.values[key] * factor;
    });
    return {
        ...point,
        values,
        reference: point.reference == null ? undefined : point.reference * factor,
        sections: point.sections?.map((section) => ({
            ...section,
            lines: section.lines.map((line) => ({ ...line, value: line.value * factor })),
            total: section.total ? { ...section.total, value: section.total.value * factor } : undefined,
        })),
        summary: point.summary ? { ...point.summary, value: point.summary.value * factor } : undefined,
    };
}
